// WasThisHelpfulPrompt — 👍 / 👎 + optional note → POST /api/help/feedback.
//
// Stays visible after submit so the operator can swap verdicts (e.g.
// hit 👍 by mistake, then 👎 with a note). Each submit creates a new
// row; we don't edit in place. The button disabled state prevents a
// double-submit spam on slow networks — a single click → pending →
// reset-after-success.

import { useState } from 'react'
import { ThumbsUp, ThumbsDown, CheckCircle2 } from 'lucide-react'

import { submitFeedback } from './api'

interface WasThisHelpfulPromptProps {
  topic: string
  stepId?: string | null
}

type Pending = 'idle' | 'submitting' | 'ok' | 'error'

export function WasThisHelpfulPrompt({ topic, stepId = null }: WasThisHelpfulPromptProps) {
  const [state, setState] = useState<Pending>('idle')
  const [note, setNote] = useState('')
  const [lastVerdict, setLastVerdict] = useState<'helpful' | 'not_helpful' | null>(null)

  const submit = async (verdict: 'helpful' | 'not_helpful') => {
    if (state === 'submitting') return
    setState('submitting')
    setLastVerdict(verdict)
    try {
      await submitFeedback({
        topic,
        step_id: stepId ?? null,
        verdict,
        note: note.trim() ? note.trim() : null,
      })
      setState('ok')
      setNote('')
    } catch {
      setState('error')
    }
  }

  return (
    <div
      data-testid="help-feedback-prompt"
      className="rounded-xl border border-hairline bg-surface-raised p-4 dark:border-slate-700 dark:bg-slate-900"
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-ink dark:text-slate-200">
          Was this helpful?
        </span>
        <button
          type="button"
          onClick={() => submit('helpful')}
          disabled={state === 'submitting'}
          data-testid="help-feedback-yes"
          aria-pressed={lastVerdict === 'helpful' && state === 'ok'}
          className="inline-flex items-center gap-1.5 rounded-full border border-hairline px-3 py-1 text-sm text-ink transition hover:border-emerald-400 hover:text-emerald-700 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:border-emerald-400 dark:hover:text-emerald-300"
        >
          <ThumbsUp aria-hidden="true" className="h-4 w-4" />
          Yes
        </button>
        <button
          type="button"
          onClick={() => submit('not_helpful')}
          disabled={state === 'submitting'}
          data-testid="help-feedback-no"
          aria-pressed={lastVerdict === 'not_helpful' && state === 'ok'}
          className="inline-flex items-center gap-1.5 rounded-full border border-hairline px-3 py-1 text-sm text-ink transition hover:border-rose-400 hover:text-rose-700 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:border-rose-400 dark:hover:text-rose-300"
        >
          <ThumbsDown aria-hidden="true" className="h-4 w-4" />
          No
        </button>
        {state === 'ok' && (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5" />
            Thanks — logged.
          </span>
        )}
        {state === 'error' && (
          <span className="text-xs text-rose-700 dark:text-rose-300">
            Couldn't save — try again.
          </span>
        )}
      </div>
      <label className="mt-3 block">
        <span className="sr-only">Optional note</span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 1000))}
          placeholder="Optional — what was unclear? Missing a step?"
          data-testid="help-feedback-note"
          rows={2}
          className="w-full rounded-md border border-hairline bg-surface-page p-2 text-sm text-ink placeholder:text-ink-soft focus:border-butler-500 focus:outline-none focus:ring-2 focus:ring-butler-500/30 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
        />
      </label>
    </div>
  )
}
